import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom"
import { fetchCampaignCollaborationsThunk } from "slices/campaigns/collaborations/thunk";
import { AppDispatch, RootState } from "store";
import PostPerformanceOffcanvas from "../collaborations/PostPerformanceOffcanvas";
import InfiniteScroll from "react-infinite-scroll-component";
import { formatNumber, generateUniqueKey } from "utils/helpers";
import Skeleton from "react-loading-skeleton";
import EyeIcon from "assets/icons/EyeIcon";
import TargetIcon from "assets/icons/TargetIcon";
import WebIcon1 from "assets/icons/WebIcon1";
import userProfilePicture from "../../../assets/images/users/user-dummy-img.jpg";
import { toggleSocialAccountView } from "slices/ui/reducer";
import DollarIcon2 from "assets/icons/DollarIcon2";

const CollaborationsTable = () => {

    const { campaignId } = useParams();
    const dispatch: AppDispatch = useDispatch();
    const [selectedCollaboration, setSelectedCollaboration] = useState<string | null>(null);

    const { tableFilters: filters } = useSelector((state: RootState) => state.campaignCollaborations);
    const [filterId, setFilterId] = useState(generateUniqueKey(filters, ['page']));

    const campaign = useSelector((state: RootState) => state.campaigns.items[campaignId!]?.data?.campaign);

    const collaborationsData = useSelector((state: RootState) => state.campaignCollaborations.campaigns[campaignId!]?.filtered_lists?.[filterId!]);
    const collaborations = collaborationsData?.list ?? [];
    const page = collaborationsData?.page ?? 1;
    const has_more = collaborationsData?.has_more ?? true;

    const loading = useSelector((state: RootState) => state.campaignCollaborations.loading);
    const error = useSelector((state: RootState) => state.campaignCollaborations.error);

    const [initialLoading, setInitialLoading] = useState(true);
    const connectionRequired = useSelector((state: RootState) => state.campaigns.items[campaignId!]?.data?.campaign?.connection_required);

    const campaignInsightsData = useSelector((state: RootState) => state.campaignInsights.campaigns[campaignId!]);

    const insights = campaignInsightsData?.insights;

    const hasClicks = (insights?.engagement?.clicks?.value ?? 0) > 0;


    useEffect(() => {
        if (campaignId) {
            setInitialLoading(true);
            dispatch(fetchCampaignCollaborationsThunk({ filters, campaignId }))
                .finally(() => {
                    setFilterId(generateUniqueKey(filters, ['page']));
                    setInitialLoading(false);
                })
        }
    }, [campaignId, filters, dispatch])

    const nextPage = () => {
        if (has_more && campaignId) {
            const nextPageNumber = page + 1;
            dispatch(fetchCampaignCollaborationsThunk({ filters: { ...filters, page: nextPageNumber }, campaignId }))
        }
    }

    return (
        <React.Fragment>
            <InfiniteScroll
                dataLength={collaborations.length}
                next={nextPage}
                hasMore={has_more}
                loader={<></>}
                className="w-100"
            >
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Views</th>
                            <th>Interactions</th>
                            <th>CPV</th>
                            <th>CPE</th>
                            {
                                campaign?.mode !== 'offline' && connectionRequired ? (
                                    <th>Total Watch Time</th>
                                ) : null
                            }
                            {
                                hasClicks ? (
                                    <th>Clicks</th>
                                ) : null
                            }
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            !initialLoading && collaborations.map((collaboration, i) => {
                                return (
                                    <tr className="border-bottom-1 px-3" key={i}>
                                        <td>
                                            <div className="py-3">
                                                <div
                                                    className="d-flex justify-content-start align-items-center gap-2 border-right-dashed td-content cursor-pointer"
                                                    onClick={() => {
                                                        if (collaboration.social_account?.uuid) {
                                                            dispatch(toggleSocialAccountView({
                                                                socialAccountId: collaboration.social_account.uuid,
                                                                influencerId: collaboration.influencer?.uuid,
                                                            }));
                                                        }
                                                    }}
                                                >
                                                    <img className="influencer-pic" src={collaboration.social_account?.profile_picture_url ?? userProfilePicture} alt="Profile Pic" />
                                                    <span className="influencer-name">{collaboration?.influencer?.name ?? (collaboration.social_account?.name ?? `@${collaboration?.social_account?.handle}`)}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                    <EyeIcon /> <span className="">{formatNumber(collaboration.views_count)}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                    <TargetIcon /> <span className="">{formatNumber(collaboration.interactions_count)}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                    <DollarIcon2 /> <span className="">{collaboration.cpv}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="py-3">
                                                <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                    <DollarIcon2 /> <span className="">{collaboration.cpe}</span>
                                                </div>
                                            </div>
                                        </td>
                                        {
                                            campaign?.mode !== 'offline' && connectionRequired ? (
                                                <td>
                                                    <div className="py-3">
                                                        {
                                                            collaboration.total_watch_time ?
                                                                (<div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                                    <EyeIcon /> <span className="">{collaboration.total_watch_time ?? '-'}</span>
                                                                </div>) : '-'
                                                        }
                                                    </div>
                                                </td>
                                            ) : null
                                        }
                                        {
                                            hasClicks ? (
                                                <td>
                                                    <div className="py-3">
                                                        <div className="d-flex justify-content-center align-items-center gap-1 border-right-dashed td-content">
                                                            <WebIcon1 /> <span className="">{formatNumber(collaboration.clicks_count ?? 0)}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                            ) : null
                                        }
                                        <td>
                                            <div className="py-3 d-flex align-items-center justify-content-end gap-2 px-3">
                                                <button className="theme-btn btn-success-light" onClick={() => setSelectedCollaboration(collaboration.uuid)}>
                                                    Post Metrics <i className='bx bx-right-arrow-alt ms-1' ></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            })
                        }
                        {
                            (loading || initialLoading) &&
                            Array.from({ length: 10 }).map((_, i) => {
                                return (
                                    <tr key={i}>
                                        <td><Skeleton /></td>
                                        <td><Skeleton /></td>
                                        <td><Skeleton /></td>
                                        <td><Skeleton /></td>
                                        <td><Skeleton /></td>
                                        <td><Skeleton /></td>
                                        {
                                            connectionRequired ? (
                                                <>
                                                    <td><Skeleton /></td>
                                                    <td><Skeleton /></td>
                                                </>
                                            ) : null
                                        }
                                    </tr>
                                )
                            })
                        }
                    </tbody>
                </table>
                {
                    !(loading || initialLoading) && error &&
                    <div className="py-3 d-flex justify-content-center align-items-center">
                        <button className="theme-btn theme-btn-sm btn-primary-outlined" onClick={nextPage}>Load More <i className='ms-2 fs-5 bx bx-refresh'></i></button>
                    </div>
                }
                {
                    !(loading || initialLoading) && collaborations.length === 0 &&
                    <div className="d-flex w-100 justify-content-center align-items-center my-5">
                        <div className="length-zero my-5">
                            <h3>Currently there is no collaboration in your list</h3>
                        </div>
                    </div>
                }
            </InfiniteScroll>
            <PostPerformanceOffcanvas
                isOpen={!!selectedCollaboration}
                onClose={() => setSelectedCollaboration(null)}
                collaborationId={selectedCollaboration}
            />
        </React.Fragment>
    )
}

export default CollaborationsTable
